import { useState, useEffect, useMemo, useCallback } from "react";
import { supabase } from "../supabaseClient";
import { clearProfileCache } from "../userProfile";
import { loadFromSupabase } from "../supabaseDB";
import ShipperShipments from "./shipper/ShipperShipments";
import PalletBreakdown from "./PalletBreakdown";
import ShipperExportCerts from "./shipper/ShipperExportCerts";
import ShipperCertifications from "./shipper/ShipperCertifications";
import ShipperProducts from "./shipper/ShipperProducts";
import ShipperGrowers from "./shipper/ShipperGrowers";
import ShipperBrokers from "./shipper/ShipperBrokers";
import ShipperCompanyProfile from "./shipper/ShipperCompanyProfile";
import ShipperUserProfile from "./shipper/ShipperUserProfile";
import GPSTracking from "./GPSTracking";
import USDALookup from "./USDALookup";
import { hasPermission } from "../permissions";

/* ═══════════════════════════════════════════════════════════════════════════
   SHIPPER PORTAL SHELL — replaces ShipperPortalLanding.

   Mirrors the admin shell visually (same logo + sticky top nav + dropdown
   pattern) but with 4 groups instead of 5/6. Every module loaded here
   receives the shipper's supplier_id as a prop so the components can
   scope their queries — the shell is the single place that resolves it.

   Default language is Spanish (most Mexican suppliers); a small ES/EN
   toggle in the header lets each user override per session.

   Logout button is in the header instead of the admin's icon strip so
   the shipper always has a one-click exit.
   ═══════════════════════════════════════════════════════════════════════════ */

const NAV = [
  { id: "operations", en: "Operations", es: "Operaciones", items: [
    { key: "shp_shipments", en: "Shipments",    es: "Embarques" },
    { key: "shp_pallet_breakdown", en: "Packing List Breakdown", es: "Detalle Packing List" },
    { key: "shp_gps",       en: "GPS Tracking", es: "Rastreo GPS" },
  ]},
  { id: "compliance", en: "Compliance", es: "Cumplimiento", items: [
    { key: "shp_export_certs",   en: "Export Certificates", es: "Certificados de Exportación" },
    { key: "shp_certifications", en: "Certifications",      es: "Certificaciones" },
    { key: "shp_usda_lookup",    en: "USDA Lookup",         es: "Consulta USDA" },
  ]},
  { id: "catalogs", en: "Catalogs", es: "Catálogos", items: [
    { key: "shp_products", en: "Products",        es: "Productos" },
    { key: "shp_growers",  en: "Growers",         es: "Productores" },
    { key: "shp_brokers",  en: "Customs Brokers", es: "Agentes Aduanales" },
  ]},
  { id: "settings", en: "Settings", es: "Configuración", items: [
    { key: "shp_company", en: "Company Profile", es: "Perfil de Empresa" },
    { key: "shp_user",    en: "User Profile",    es: "Mi Perfil" },
  ]},
];

/* Maps each navigation item to its required view permission. Items
   not in this map are always visible (e.g. the user's own profile).
   When the logged-in user lacks the listed permission, the item is
   filtered out of the menu AND attempts to navigate to it from URL
   show the "Access denied" placeholder. */
const ITEM_VIEW_PERMISSION = {
  shp_shipments:      "view_shipments",
  shp_gps:            "view_gps",
  shp_export_certs:   "view_export_certs",
  shp_certifications: "view_certifications",
  shp_usda_lookup:    "view_usda_lookup",
  shp_products:       "view_products",
  shp_growers:        "view_growers",
  shp_brokers:        "view_brokers",
  shp_company:        "view_company_profile",
  shp_user:           null,  // always visible — own profile
};

const T = {
  en: {
    welcome: "Welcome",
    logout: "Sign out",
    portal: "Shipper Portal",
    pendingShipments: "pending review",
    noSupplier: "No supplier is linked to this account yet. Contact GFI to finish setup.",
    accessDenied: "Access denied",
    accessDeniedSub: "You don't have permission to view this module. Contact your administrator.",
  },
  es: {
    welcome: "Bienvenido",
    logout: "Cerrar sesión",
    portal: "Portal del Exportador",
    pendingShipments: "en revisión",
    noSupplier: "Aún no hay proveedor asociado a esta cuenta. Contacta a GFI para completar la configuración.",
    accessDenied: "Acceso denegado",
    accessDeniedSub: "No tienes permiso para ver este módulo. Contacta a tu administrador.",
  },
};

function GFILogo() {
  return (
    <svg width="56" height="56" viewBox="-55 -55 110 110" style={{ flexShrink: 0 }}>
      <defs>
        <linearGradient id="logoGl-sps" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="rgba(255,255,255,0.25)" />
          <stop offset="40%" stopColor="rgba(255,255,255,0.05)" />
          <stop offset="100%" stopColor="rgba(0,0,0,0.15)" />
        </linearGradient>
      </defs>
      <rect x="-35" y="-35" width="70" height="70" rx="16" fill="#1B3A35" />
      <rect x="-35" y="-35" width="70" height="70" rx="16" fill="url(#logoGl-sps)" />
      <text x="0" y="2" textAnchor="middle" fontFamily="'JetBrains Mono', monospace" fontSize="22" fontWeight="700" fill="#5BBCB0" letterSpacing="3">GFI</text>
      <rect x="-16" y="10" width="32" height="1.2" rx="0.6" fill="#3D9B8F" />
      <text x="0" y="22" textAnchor="middle" fontFamily="'DM Sans', sans-serif" fontSize="8" fontWeight="500" fill="#3D9B8F" letterSpacing="3">NEXUS</text>
    </svg>
  );
}

/* Welcome landing shown once per session when the portal opens. Shows
   the shipper's logo (soft fade-in, no sound), a greeting with the
   shipper name, and quick-access buttons to every module the user has
   permission to see (same NAV + permission filter as the top menu). */
function WelcomeScreen({ supplier, profile, lang, onPick }) {
  const [fadeIn, setFadeIn] = useState(false);
  useEffect(function () {
    const id = setTimeout(function () { setFadeIn(true); }, 50);
    return function () { clearTimeout(id); };
  }, []);
  const shipperName = (supplier && (supplier.shortName || supplier.fullName)) || "";
  const logoUrl = supplier && supplier.logoUrl;
  const quick = [];
  NAV.forEach(function (group) {
    group.items.forEach(function (item) {
      const permKey = ITEM_VIEW_PERMISSION[item.key];
      const visible = (permKey === null || permKey === undefined) ? true : hasPermission(profile, permKey);
      if (visible) quick.push(item);
    });
  });
  return (
    <div style={{ maxWidth: 880, margin: "0 auto", padding: "48px 24px", textAlign: "center" }}>
      <div style={{ opacity: fadeIn ? 1 : 0, transform: fadeIn ? "translateY(0)" : "translateY(8px)", transition: "opacity 0.8s ease, transform 0.8s ease" }}>
        {logoUrl
          ? <img src={logoUrl} alt={shipperName} style={{ maxHeight: 140, maxWidth: 240, objectFit: "contain", margin: "0 auto 20px", display: "block" }} />
          : <div style={{ height: 120, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 40, fontWeight: 800, color: "var(--color-accent)", marginBottom: 20 }}>{(shipperName || "GFI").slice(0, 2).toUpperCase()}</div>}
      </div>
      <h1 style={{ fontSize: 26, fontWeight: 800, color: "var(--color-text)", margin: "0 0 6px" }}>
        {(lang === "es" ? "Bienvenido" : "Welcome") + ((profile && profile.first_name) ? ", " + profile.first_name : "")}
      </h1>
      <p style={{ fontSize: 13, color: "var(--color-text-muted)", margin: "0 0 32px" }}>
        {lang === "es" ? "Selecciona un módulo para comenzar" : "Pick a module to get started"}
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12 }}>
        {quick.map(function (item) {
          return (
            <button key={item.key} onClick={function () { onPick(item.key); }}
              style={{ padding: "18px 14px", borderRadius: 10, border: "1px solid var(--color-border)", background: "var(--color-bg-card)", color: "var(--color-text)", cursor: "pointer", fontSize: 13, fontWeight: 600, transition: "border-color 0.15s, transform 0.1s" }}
              onMouseEnter={function (e) { e.currentTarget.style.borderColor = "var(--color-accent)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
              onMouseLeave={function (e) { e.currentTarget.style.borderColor = "var(--color-border)"; e.currentTarget.style.transform = "translateY(0)"; }}
            >
              {lang === "es" ? item.es : item.en}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function ShipperPortalShell({ lang: initialLang, profile }) {
  const [lang, setLang] = useState(initialLang || "es");
  const [module, setModule] = useState(() => localStorage.getItem("gfi-shp-module") || "shp_shipments");
  const [pendingLot, setPendingLot] = useState(null);
  const [returnModule, setReturnModule] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [supplier, setSupplier] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [showWelcome, setShowWelcome] = useState(true);

  const t = T[lang === "en" ? "en" : "es"];
  /* `profile.supplier_id` is a UUID (auth.users-style); the supabaseDB
     layer maps regular records as `record.id = legacy_id` and
     `record._uuid = uuid`. So we ALWAYS resolve the supplier by uuid
     first, then thread its LEGACY id down to children — that's what
     every other table's foreign key (shipments.supplier_id resolved
     to record.supplierId, growers.shipperId, etc.) actually stores
     on the app-side record. Passing the uuid raw would never match. */
  const profileSupplierUuid = profile && profile.supplier_id;

  useEffect(function () { localStorage.setItem("gfi-shp-module", module); }, [module]);

  /* PalletBreakdown → Shipments deep link (shipper portal side): click
     on a Lot cell dispatches this event, we switch to the shipments
     module and stash the target lot for ShipperShipments → ShipperPortal
     to open once its embarques have loaded. Remembers the module we
     came from so closing the embarque can return to it. */
  useEffect(function () {
    function onOpenShipment(e) {
      setReturnModule(module);
      setModule("shp_shipments");
      setPendingLot(e.detail && e.detail.lot);
    }
    window.addEventListener("gfi-open-shipment", onOpenShipment);
    return function () { window.removeEventListener("gfi-open-shipment", onOpenShipment); };
  }, [module]);

  /* Fires when ShipperPortal closes an embarque that was opened via
     the deep link above — returns to whatever module we came from. */
  useEffect(function () {
    function onClose() {
      if (returnModule) {
        setModule(returnModule);
        setReturnModule(null);
        setPendingLot(null);
      }
    }
    window.addEventListener("gfi-shipment-closed", onClose);
    return function () { window.removeEventListener("gfi-shipment-closed", onClose); };
  }, [returnModule]);

  /* Resolve the supplier record once — every module needs its address /
     RFC / contacts (CompanyProfile reads it directly, the others use it
     to pre-fill new records). Match by uuid AND by legacy id as a
     defensive fallback for any future migration that stores legacy
     ids in user_profiles. */
  useEffect(function () {
    if (!profileSupplierUuid) { setSupplier(null); return; }
    let alive = true;
    loadFromSupabase("suppliers").then(function (rows) {
      if (!alive) return;
      const target = String(profileSupplierUuid);
      const sup = (rows || []).find(function (s) {
        return String(s._uuid) === target || String(s.id) === target;
      });
      setSupplier(sup || null);
    }).catch(function () { if (alive) setSupplier(null); });
    return function () { alive = false; };
  }, [profileSupplierUuid]);

  /* Legacy-id form of the supplier id — null until the supplier resolves.
     Every child module compares against this; matches record.supplierId
     (legacy) on shipments, export_certs, certifications, growers. */
  const supplierId = supplier ? supplier.id : null;

  /* Preload every legacy `gfi-*` cache that any shipper module
     reads synchronously. The wrapped admin modules (ShipperPortal,
     ExportCertificateRegistry, CertificationRegistry,
     CustomsBrokerRegistry) all use loadLS() inside helpers that
     decide which brokers to expose, which invoice prefix to apply,
     and how many uses each export cert has left. Without this
     preload, a freshly-signed-in shipper sees empty localStorage
     until each module's own loader catches up — so brokers don't
     appear (FIX 28), the invoice prefix doesn't render (FIX 29),
     and the cert usage counts read as zero across the board
     (FIX 30). The admin shell does the same thing in App.js. */
  useEffect(function () {
    if (!supplierId) return;
    let alive = true;
    const pairs = [
      ["suppliers",         "gfi-suppliers"],
      ["brokers",           "gfi-brokers"],
      ["products",          "gfi-products"],
      ["warehouses",        "gfi-warehouses"],
      ["growers",           "gfi-growers"],
      ["certifications",    "gfi-certifications"],
      ["export_certs",      "gfi-export-certs"],
      ["shipper_settings",  "gfi-shipper-settings"],
      ["shipments",         "gfi-shipments"],
      ["bills",             "gfi-bills"],
      ["shipper_shipments", "gfi-shipper-shipments"],
      ["seasons",           "gfi-seasons"],
      ["company_profile",   "gfi-company-profile"],
    ];
    Promise.all(pairs.map(function (p) {
      return loadFromSupabase(p[0])
        .then(function (rows) {
          if (!alive) return;
          /* gfi-shipper-settings is a single object keyed by
             supplierId, not an array. Convert the row list into
             that map shape so the existing getShipperSettings
             helper finds the entry.
             
             FIX: Index the map by EVERY possible id variant so
             getShipperSettings(shipper.id) always finds the entry
             regardless of whether shipper.id is a legacy id, a
             UUID, or the supplier_id column value. Previously only
             r.id (legacy_id of the settings row itself) was used
             as the key, which never matched shipper.id (legacy_id
             of the supplier). */
          try {
            if (p[0] === "shipper_settings") {
              const map = {};
              (rows || []).forEach(function (r) {
                if (!r) return;
                /* Store under every possible key so lookups by
                   legacy supplier id, UUID, or settings row id
                   all find this record. */
                var keys = [
                  r.supplierId,
                  r.supplier_id,
                  r._uuid,
                  r.id,
                  r.supplierLegacyId,
                  r.supplier_legacy_id,
                ];
                keys.forEach(function (k) {
                  if (k != null && String(k) !== "null" && String(k) !== "undefined") {
                    map[String(k)] = r;
                  }
                });
              });
              localStorage.setItem(p[1], JSON.stringify(map));
            } else {
              localStorage.setItem(p[1], JSON.stringify(rows || []));
            }
          } catch (e) { /* quota / serialization — non-fatal */ }
        })
        .catch(function () { /* leave whatever stale value is there */ });
    }));
    return function () { alive = false; };
  }, [supplierId]);

  /* Refresh the "pending review" badge whenever the shell mounts or
     the user comes back from another tab. Counts the shipper's own
     submitted shipments that haven't been accepted/rejected yet. */
  const refreshPending = useCallback(function () {
    if (!supplierId) { setPendingCount(0); return; }
    loadFromSupabase("shipments").then(function (rows) {
      const n = (rows || []).filter(function (s) {
        return String(s.supplierId) === String(supplierId) && s.shipperStatus === "submitted";
      }).length;
      setPendingCount(n);
    }).catch(function () {});
  }, [supplierId]);
  useEffect(function () {
    refreshPending();
    window.addEventListener("focus", refreshPending);
    return function () { window.removeEventListener("focus", refreshPending); };
  }, [refreshPending]);

  /* Close dropdowns on outside click. */
  useEffect(function () {
    if (!openDropdown) return;
    const h = function (e) {
      if (!e.target.closest || !e.target.closest(".sps-nav-group")) setOpenDropdown(null);
    };
    const k = function (e) { if (e.key === "Escape") setOpenDropdown(null); };
    document.addEventListener("mousedown", h);
    document.addEventListener("keydown", k);
    return function () {
      document.removeEventListener("mousedown", h);
      document.removeEventListener("keydown", k);
    };
  }, [openDropdown]);

  const handleLogout = async function () {
    try { await supabase.auth.signOut(); } catch (e) {}
    clearProfileCache();
  };

  const activeGroupId = useMemo(function () {
    for (const g of NAV) {
      if (g.items && g.items.some(function (i) { return i.key === module; })) return g.id;
    }
    return null;
  }, [module]);

  const renderModule = function () {
    /* Three states: (1) no supplier linked on the profile at all →
       contact-GFI message; (2) profile has supplier_id but the
       suppliers fetch hasn't resolved yet → quiet placeholder while
       loading; (3) resolved → render the module. */
    if (!profileSupplierUuid) {
      return (
        <div style={{ padding: 32, textAlign: "center", color: "var(--color-text-muted)", fontSize: 14, maxWidth: 480, margin: "60px auto" }}>
          {t.noSupplier}
        </div>
      );
    }
    if (!supplierId) {
      return (
        <div style={{ padding: 32, textAlign: "center", color: "var(--color-text-muted)", fontSize: 13 }}>…</div>
      );
    }
    const ctx = { supplierId: supplierId, supplier: supplier, profile: profile, lang: lang, onShipmentChange: refreshPending };
    /* Permission guard: if the user lacks view permission for the
       current module, show an access-denied placeholder instead of
       the actual component. */
    const requiredPerm = ITEM_VIEW_PERMISSION[module];
    if (requiredPerm && !hasPermission(profile, requiredPerm)) {
      return (
        <div style={{ padding: "60px 24px", textAlign: "center", maxWidth: 480, margin: "0 auto" }}>
          <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }}>🔒</div>
          <h2 style={{ margin: "0 0 8px", fontSize: 20, fontWeight: 700, color: "var(--color-text)" }}>{t.accessDenied}</h2>
          <p style={{ margin: 0, fontSize: 13, color: "var(--color-text-muted)" }}>{t.accessDeniedSub}</p>
        </div>
      );
    }
    switch (module) {
      case "shp_shipments":      return <ShipperShipments {...ctx} openLot={pendingLot} onLotOpened={() => setPendingLot(null)} />;
      case "shp_pallet_breakdown": return <PalletBreakdown lang={lang} lockSupplierId={supplierId} />;
      case "shp_gps":            return <GPSTracking lang={lang} lockSupplierId={supplierId} />;
      case "shp_export_certs":   return <ShipperExportCerts {...ctx} />;
      case "shp_certifications": return <ShipperCertifications {...ctx} />;
      case "shp_products":       return <ShipperProducts {...ctx} />;
      case "shp_growers":        return <ShipperGrowers {...ctx} />;
      case "shp_brokers":        return <ShipperBrokers {...ctx} />;
      case "shp_company":        return <ShipperCompanyProfile {...ctx} />;
      case "shp_user":           return <ShipperUserProfile {...ctx} />;
      case "shp_usda_lookup":    return <USDALookup lang={lang} />;
      default:                   return <ShipperShipments {...ctx} />;
    }
  };

  const firstName = (profile && profile.first_name) || "";
  const greeting = firstName ? (t.welcome + ", " + firstName) : t.welcome;

  return (
    <div className="sps-shell">
      <header className="sps-header">
        <div className="sps-header-left" onClick={function () { setShowWelcome(true); }} style={{ cursor: "pointer" }}>
          <GFILogo />
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ fontSize: 11, letterSpacing: "0.15em", color: "#5BBCB0", fontWeight: 700, textTransform: "uppercase" }}>GFI Nexus</span>
            <span style={{ fontSize: 14, fontWeight: 600, color: "#ffffff" }}>{t.portal}</span>
          </div>
          {supplier && supplier.logoUrl && (
            <img src={supplier.logoUrl} alt="" style={{ height: 38, maxWidth: 90, objectFit: "contain", marginLeft: 12, borderRadius: 6, background: "rgba(255,255,255,0.92)", padding: 3 }} />
          )}
        </div>

        <nav className="sps-nav" onMouseLeave={function () { setOpenDropdown(null); }}>
          {NAV.map(function (group) {
            const label = lang === "es" ? group.es : group.en;
            const isActive = activeGroupId === group.id;
            const isOpen = openDropdown === group.id;
            const showBadge = group.id === "operations" && pendingCount > 0;
            /* Filter items based on the user's view permissions. If
               the item maps to a permission key the user doesn't
               have, hide it. Items mapped to null are always shown
               (e.g. the user's own profile). */
            const visibleItems = group.items.filter(function (item) {
              const permKey = ITEM_VIEW_PERMISSION[item.key];
              if (permKey === null) return true;
              if (permKey === undefined) return true;  // safety: unknown items default to visible
              return hasPermission(profile, permKey);
            });
            /* Hide the entire group dropdown if no items are visible. */
            if (visibleItems.length === 0) return null;
            return (
              <div
                key={group.id}
                className={"sps-nav-group" + (isActive ? " active" : "") + (isOpen ? " open" : "")}
                onMouseEnter={function () { setOpenDropdown(group.id); }}
              >
                <button
                  className="sps-nav-trigger"
                  onClick={function () { setOpenDropdown(function (p) { return p === group.id ? null : group.id; }); }}
                >
                  {label}
                  {showBadge && (
                    <span title={pendingCount + " " + t.pendingShipments} style={{ marginLeft: 6, background: "#F59E0B", color: "#fff", borderRadius: 10, padding: "0 6px", fontSize: 10, fontWeight: 700, minWidth: 16, display: "inline-block", textAlign: "center" }}>{pendingCount}</span>
                  )}
                  <span className="sps-nav-caret">▾</span>
                </button>
                <div className="sps-nav-dropdown">
                  {visibleItems.map(function (item) {
                    return (
                      <button
                        key={item.key}
                        className={"sps-nav-item" + (module === item.key ? " active" : "")}
                        onClick={function () { setModule(item.key); setOpenDropdown(null); setShowWelcome(false); }}
                      >
                        {lang === "es" ? item.es : item.en}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>

        <div className="sps-header-right">
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.85)", marginRight: 4 }}>{greeting}</div>
          <div style={{ display: "inline-flex", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 6, overflow: "hidden" }}>
            {["es", "en"].map(function (k) {
              const isOn = lang === k;
              return (
                <button
                  key={k}
                  onClick={function () { setLang(k); }}
                  style={{
                    background: isOn ? "rgba(91,188,176,0.18)" : "transparent",
                    color: isOn ? "#5BBCB0" : "rgba(255,255,255,0.7)",
                    border: "none", padding: "5px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.05em",
                  }}
                >{k}</button>
              );
            })}
          </div>
          <button
            onClick={handleLogout}
            title={t.logout}
            style={{
              background: "transparent", border: "1px solid rgba(91,188,176,0.4)",
              color: "#5BBCB0", padding: "6px 12px", borderRadius: 6,
              fontSize: 12, fontWeight: 600, cursor: "pointer",
            }}
          >{t.logout}</button>
        </div>
      </header>

      <main className="sps-main">
        {showWelcome && supplierId
          ? <WelcomeScreen supplier={supplier} profile={profile} lang={lang} onPick={function (k) { setModule(k); setShowWelcome(false); }} />
          : renderModule()}
      </main>
    </div>
  );
}
